// ─── Frei setzbare Texte der Installation ────────────────────────────────────
//
// Was hier steht, kann der Betreiber in der Konsole ändern, ohne dass jemand
// den Quelltext anfasst oder neu baut. Gedacht für das, was von Installation zu
// Installation verschieden ist: der Name, mit dem sich die Anwendung meldet,
// der Hinweis auf der Anmeldeseite, die Fußzeile mit Impressum.
//
// Bewusst eine ENDLICHE Liste und kein freies Schlüssel-Wert-Lager. Ein Lager,
// in das jeder schreiben kann, füllt sich mit Schlüsseln, die niemand mehr
// zuordnet – und die Anwendung müsste für jeden unbekannten Schlüssel einen
// Rückfall bereithalten. Hier weiß beide Seiten, was es gibt.
//
// Jeder Text hat eine VORGABE. Sie ist das, was ohne Zutun erscheint; ein
// leeres Feld in der Konsole bedeutet „Vorgabe benutzen", nicht „nichts
// anzeigen". Eine Anwendung, die nach dem Leeren eines Feldes namenlos wird,
// wäre eine Falle.

export interface TextDefinition {
  key: string;
  label: string;
  /** Wo der Text erscheint – hilft beim Einordnen in der Konsole. */
  ort: string;
  vorgabe: string;
  /** Mehrzeiliges Eingabefeld statt einer Zeile. */
  lang?: boolean;
  /** Grobe Einordnung für die Darstellung. */
  gruppe: 'Anwendung' | 'Anmeldung' | 'Rechtliches' | 'Benachrichtigungen';
  /**
   * Platzhalter, die in diesem Text eingesetzt werden – ohne die geschweiften
   * Klammern. Die Konsole zeigt sie an; ohne diese Liste müsste man raten,
   * was man schreiben darf.
   */
  platzhalter?: string[];
}

// ─── Platzhalter ─────────────────────────────────────────────────────────────

/**
 * Ersetzt `{name}` durch den zugehörigen Wert.
 *
 * Unbekannte Platzhalter bleiben unangetastet stehen. Das ist Absicht: wer
 * sich vertippt, soll `{stammbam}` im Postfach sehen und den Fehler bemerken.
 * Sie stillschweigend zu leeren hieße, dass eine Mail mit einem klaffenden
 * Loch hinausgeht und niemand weiß, warum.
 */
export function setzePlatzhalter(
  vorlage: string,
  werte: Record<string, string>,
): string {
  return vorlage.replace(/\{(\w+)\}/g, (ganzes, name: string) =>
    Object.prototype.hasOwnProperty.call(werte, name) ? werte[name] : ganzes,
  );
}

export const TEXTE: TextDefinition[] = [
  // ─── Anwendung ────────────────────────────────────────────────────────────
  {
    key: 'app.name',
    label: 'Name der Anwendung',
    ort: 'Kopfzeile, Browser-Titel, E-Mails',
    vorgabe: 'Stammbaum',
    gruppe: 'Anwendung',
  },
  {
    key: 'app.tagline',
    label: 'Untertitel',
    ort: 'Anmeldeseite, unter dem Namen',
    vorgabe: 'Die Geschichte Ihrer Familie',
    gruppe: 'Anwendung',
  },
  {
    key: 'app.welcome',
    label: 'Begrüßung',
    ort: 'Leerer Stammbaum, bevor die erste Person angelegt ist',
    vorgabe: 'Noch keine Personen vorhanden',
    gruppe: 'Anwendung',
  },
  {
    key: 'app.welcomeHint',
    label: 'Hinweis zur Begrüßung',
    ort: 'unter der Begrüßung',
    vorgabe: 'Lege die erste Person an oder importiere eine GEDCOM-Datei.',
    lang: true,
    gruppe: 'Anwendung',
  },
  {
    key: 'app.footer',
    label: 'Fußzeile',
    ort: 'unten in der Anwendung',
    vorgabe: '',
    lang: true,
    gruppe: 'Anwendung',
  },

  // ─── Anmeldung ────────────────────────────────────────────────────────────
  {
    key: 'login.hint',
    label: 'Hinweis auf der Anmeldeseite',
    ort: 'unter dem Anmeldeformular',
    vorgabe: '',
    lang: true,
    gruppe: 'Anmeldung',
  },
  {
    key: 'login.supportContact',
    label: 'Wohin bei Problemen',
    ort: 'Anmeldeseite und Fehlermeldungen',
    vorgabe: '',
    gruppe: 'Anmeldung',
  },

  // ─── Benachrichtigungen ───────────────────────────────────────────────────
  //
  // Nimmt der Betreiber Zugriff auf einen fremden Stammbaum, erfährt dessen
  // Besitzer davon. Vorher stand es nur im Protokoll – also dort, wo man
  // nachsieht, wenn man schon einen Verdacht hat.
  //
  // Der Wortlaut gehört dem Betreiber: er weiß, wie er mit seinen Kunden
  // spricht, und er haftet für das, was er ihnen schreibt. Die Vorgabe ist
  // knapp und sachlich – sie soll niemanden erschrecken und nichts
  // beschönigen.
  {
    key: 'mail.betriebszugriff.betreff',
    label: 'Betreiberzugriff: Betreff',
    ort: 'E-Mail an den Besitzer, sobald ein Betreiber Zugriff nimmt',
    vorgabe: 'Zugriff auf Ihren Stammbaum „{stammbaum}"',
    gruppe: 'Benachrichtigungen',
    platzhalter: ['stammbaum', 'app'],
  },
  {
    key: 'mail.betriebszugriff.text',
    label: 'Betreiberzugriff: Text',
    ort: 'E-Mail an den Besitzer, sobald ein Betreiber Zugriff nimmt',
    vorgabe:
      'für Ihren Stammbaum „{stammbaum}" wurde ein befristeter Zugang für den Betrieb ' +
      'eingerichtet.\n\n' +
      'Angegebener Grund: {grund}\n' +
      'Eingerichtet von: {betreiber}\n' +
      'Gültig bis: {bis}\n\n' +
      'Der Zugang endet zum genannten Zeitpunkt von selbst. Gesundheitsdaten sind davon ' +
      'ausgenommen. Was in dieser Zeit geschieht, steht im Protokoll Ihres Stammbaums.\n\n' +
      'Wenn Sie diesen Zugang nicht erwartet haben, melden Sie sich bitte bei uns.',
    lang: true,
    gruppe: 'Benachrichtigungen',
    platzhalter: ['stammbaum', 'grund', 'betreiber', 'bis', 'app'],
  },

  // Setzt der Betreiber den zweiten Faktor eines Kontos zurück, erfährt der
  // Kontoinhaber davon. Das ist der einzige Vorgang, bei dem jemand anderes den
  // Schutz eines fremden Kontos abschaltet – wer die Mail nicht erwartet hat,
  // muss sofort merken, dass etwas nicht stimmt.
  {
    key: 'mail.zweifaktor_zurueckgesetzt.betreff',
    label: 'Zwei-Faktor zurückgesetzt: Betreff',
    ort: 'E-Mail an den Kontoinhaber, nachdem der Betreiber den zweiten Faktor entfernt hat',
    vorgabe: 'Ihre Zwei-Faktor-Anmeldung wurde zurückgesetzt',
    gruppe: 'Benachrichtigungen',
    platzhalter: ['grund', 'betreiber', 'app'],
  },
  {
    key: 'mail.zweifaktor_zurueckgesetzt.text',
    label: 'Zwei-Faktor zurückgesetzt: Text',
    ort: 'E-Mail an den Kontoinhaber, nachdem der Betreiber den zweiten Faktor entfernt hat',
    vorgabe:
      'für Ihr Konto bei {app} wurde die Zwei-Faktor-Anmeldung zurückgesetzt. Entfernt wurden ' +
      'die Authenticator-App, alle Ersatzcodes und alle hinterlegten Passkeys.\n\n' +
      'Angegebener Grund: {grund}\n' +
      'Zurückgesetzt von: {betreiber}\n\n' +
      'Sie melden sich ab sofort wieder allein mit Ihrem Passwort an. Bitte richten Sie den ' +
      'zweiten Faktor bei nächster Gelegenheit neu ein – bis dahin schützt nur Ihr Passwort ' +
      'Ihre Familiendaten.\n\n' +
      'Wenn Sie darum nicht gebeten haben, melden Sie sich bitte umgehend bei uns und ändern ' +
      'Sie Ihr Passwort.',
    lang: true,
    gruppe: 'Benachrichtigungen',
    platzhalter: ['grund', 'betreiber', 'app'],
  },

  // ─── Rechtliches ──────────────────────────────────────────────────────────
  {
    key: 'legal.imprint',
    label: 'Impressum',
    ort: 'Fußzeile, als eigene Seite',
    vorgabe: '',
    lang: true,
    gruppe: 'Rechtliches',
  },
  {
    key: 'legal.privacy',
    label: 'Datenschutzhinweis',
    ort: 'Fußzeile, als eigene Seite',
    vorgabe: '',
    lang: true,
    gruppe: 'Rechtliches',
  },

  // Die Hausordnung ist kein Beiwerk: Sie ist die Grundlage, auf die sich der
  // Betreiber beruft, wenn er einen Beitrag ausblendet – und sie muss vorher
  // dagestanden haben. Deshalb steht die Vorgabe hier vollständig und nicht als
  // leeres Feld: eine unausgefüllte Hausordnung wäre schlechter als eine
  // allgemeine.
  {
    key: 'forum.hausordnung',
    label: 'Forum: Hausordnung',
    ort: 'Beim ersten Betreten des Forums, mit Zustimmung. Danach jederzeit abrufbar.',
    vorgabe:
      'Dieses Forum ist für Menschen, die an ihrer Familiengeschichte arbeiten. ' +
      'Damit das funktioniert, gelten ein paar Regeln.\n\n' +
      '**Umgang.** Sachlich bleiben, auch bei Widerspruch. Wer eine Frage stellt, hat sie ' +
      'nicht schon beantwortet; wer antwortet, tut es freiwillig.\n\n' +
      '**Fremde Daten.** Über lebende Personen keine Geburtsdaten, Adressen, Krankheiten ' +
      'oder Bilder ohne deren Einverständnis. Das gilt auch für Verwandte. Aus dem ' +
      'Stammbaum wird hier nichts automatisch sichtbar – was hier steht, hast du bewusst ' +
      'hierher geschrieben.\n\n' +
      '**Anhänge.** Nur, was du zeigen darfst. Bei Archivscans die Bedingungen des Archivs ' +
      'beachten.\n\n' +
      '**Keine Werbung.** Auch keine getarnte.\n\n' +
      '**Was passiert, wenn du dein Konto löschst.** Deine Beiträge bleiben stehen, dein ' +
      'Anzeigename verschwindet – dort steht dann „gelöschtes Konto". Ein Verlauf, aus dem ' +
      'jede zweite Antwort fehlt, wäre für alle anderen wertlos. Wenn du damit nicht ' +
      'einverstanden bist, schreibe hier bitte nichts.\n\n' +
      '**Wenn etwas nicht stimmt.** Jeder Beitrag hat „melden". Der Betrieb sieht sich das ' +
      'an und kann Beiträge ausblenden oder das Schreiben befristet sperren.\n\n' +
      '**Fehler in der Anwendung** gehören nicht hierher, sondern in „Vorschlag oder Fehler ' +
      'melden" – dort gehen sie nicht unter.',
    lang: true,
    gruppe: 'Rechtliches',
  },
];

const NACH_SCHLUESSEL = new Map(TEXTE.map((t) => [t.key, t]));

export function istTextSchluessel(key: string): boolean {
  return NACH_SCHLUESSEL.has(key);
}

/** Alle Texte mit ihren Vorgaben – der Ausgangszustand. */
export function vorgabeTexte(): Record<string, string> {
  const raus: Record<string, string> = {};
  for (const t of TEXTE) raus[t.key] = t.vorgabe;
  return raus;
}

/**
 * Gepflegte Werte über die Vorgaben legen.
 *
 * Ein leerer gepflegter Wert zählt als „nicht gesetzt" und lässt die Vorgabe
 * stehen – außer die Vorgabe ist selbst leer, dann bleibt es leer. So kann man
 * ein Feld gefahrlos leeren, um zum Ausgangszustand zurückzukehren.
 */
export function mischeTexte(gepflegt: Record<string, string>): Record<string, string> {
  const raus = vorgabeTexte();
  for (const [key, wert] of Object.entries(gepflegt)) {
    if (!NACH_SCHLUESSEL.has(key)) continue;
    if (wert.trim() === '') continue;
    raus[key] = wert;
  }
  return raus;
}
